import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface LazySvgProps extends React.SVGProps<SVGSVGElement> {
  name: string
  className?: string
  containsPath?: boolean
}

const svgCache: Record<string, string> = {}
const FALLBACK_SVG_PATH = '/icons/ivr.svg'

// Helper function to validate if a URL is a proper SVG URL
const isValidSvgUrl = (url: string): boolean => {
  try {
    // Check if URL ends with .svg
    return /\.svg(\?.*)?$/.test(url) && !/\/\.svg$/.test(url)
  } catch (e) {
    return false
  }
}

export const LazySvg: React.FC<LazySvgProps> = ({
  name,
  className,
  containsPath = true,
  ...props
}) => {
  const [svgContent, setSvgContent] = useState<string | null>(null)

  useEffect(() => {
    const loadSvg = async () => {
      if (svgCache[name]) {
        setSvgContent(svgCache[name])
        return
      }

      // Use the full path if containsPath is true, otherwise try fallback paths.
      const paths = containsPath
        ? [name]
        : [`/storage/navigation-icons/${name}.svg`, `/storage/${name}.svg`]

      let fallbackNeeded = true

      for (const path of paths) {
        try {
          const response = await fetch(path, { cache: 'force-cache' })

          // Validate the response URL
          const responseUrl = response.url
          if (!isValidSvgUrl(responseUrl)) {
            continue
          }

          if (response.ok && response.status === 200) {
            const text = await response.text()
            svgCache[name] = text
            setSvgContent(text)
            fallbackNeeded = false
            break
          }
        } catch (error) {
          // Silent fail and continue to next path or fallback
        }
      }

      // If none of the paths worked, use the fallback
      if (fallbackNeeded) {
        try {
          // Check if we already have the fallback in cache
          if (svgCache[FALLBACK_SVG_PATH]) {
            setSvgContent(svgCache[FALLBACK_SVG_PATH])
          } else {
            const response = await fetch(FALLBACK_SVG_PATH)
            // Only use fallback if URL is valid
            if (response.ok && isValidSvgUrl(response.url)) {
              const text = await response.text()
              svgCache[FALLBACK_SVG_PATH] = text
              setSvgContent(text)
            } else {
              // If even the fallback fails or is invalid, set to null
              setSvgContent(null)
            }
          }
        } catch (error) {
          setSvgContent(null)
        }
      }
    }

    loadSvg()
  }, [name, containsPath])

  if (!svgContent) {
    return (
      <div>
        <svg
          className={'rounded-lg !text-border ' + className}
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="0" y="0" width="100" height="100" rx="8" ry="8" fill="currentColor">
            <animate
              attributeName="opacity"
              values="0.5;1;0.5"
              dur="1.5s"
              repeatCount="indefinite"
            />
          </rect>
        </svg>
      </div>
    )
  }

  // Parse the SVG content
  const parser = new DOMParser()
  const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml')
  const svgElement = svgDoc.documentElement

  // Apply the className and other props
  svgElement.setAttribute('class', className || '')
  Object.entries(props).forEach(([key, value]) => {
    if (typeof value === 'string' || typeof value === 'number') {
      svgElement.setAttribute(key, value.toString())
    }
  })

  // Convert the SVG element back to a string
  const serializer = new XMLSerializer()
  const svgString = serializer.serializeToString(svgElement)

  return <span dangerouslySetInnerHTML={{ __html: svgString }} />
}

export const LanguageSensitiveLazySvg: React.FC<LazySvgProps> = (props) => {
  const { i18n } = useTranslation()
  const [key, setKey] = useState(0)

  useEffect(() => {
    setKey((prevKey) => prevKey + 1)
  }, [i18n.language])

  return <LazySvg key={key} {...props} />
}
